import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { PageLoader } from '../components/Loading';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users,
  Search,
  Phone,
  Mail,
  MapPin,
  ShoppingBag,
  Gift,
  Clock,
  ChevronRight,
  X,
  Building2,
  CheckCircle,
  AlertCircle,
  Lock,
  Trash2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomersPage() {
  const { isMD } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers-full`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load customers');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCustomerDetails = async (customer) => {
    setSelectedCustomer(customer);
    setDetailsOpen(true);
    setDetailsLoading(true);
    
    try {
      const response = await axios.get(`${API}/customers/${customer.id}/details`);
      setCustomerDetails(response.data);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedCustomer(null);
    setCustomerDetails(null);
  };

  const openDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    setDeletingCustomer(true);
    try {
      await axios.delete(`${API}/customers/${customerToDelete.id}`);
      setCustomers((prev) => prev.filter((customer) => customer.id !== customerToDelete.id));
      if (selectedCustomer?.id === customerToDelete.id) {
        closeDetails();
      }
      toast.success('Customer details deleted successfully');
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete customer details');
    } finally {
      setDeletingCustomer(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <PageLoader />;

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="customers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-4xl text-lux-text mb-2">
              {isMD ? 'Customers' : 'My Customers'}
            </h1>
            <p className="text-lux-muted text-sm sm:text-base">
              {isMD 
                ? 'View customer information, purchases and referrals' 
                : 'View your customers\' information and purchases'
              }
            </p>
          </div>
          {!isMD && customers.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-900/20 border border-blue-900/30 rounded-lg text-sm text-blue-400">
              <Lock className="w-4 h-4" />
              Showing only your customers
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-lux-muted" />
          <Input
            placeholder="Search by name, phone or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="lux-input pl-10"
            data-testid="customer-search-input"
          />
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="lux-card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-900/30 text-blue-400">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="text-lux-muted text-xs">Total Customers</p>
                <p className="text-lux-text font-heading text-xl">{customers.length}</p>
              </div>
            </div>
          </div>
          <div className="lux-card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-900/30 text-green-400">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div>
                <p className="text-lux-muted text-xs">With Purchases</p>
                <p className="text-lux-text font-heading text-xl">
                  {customers.filter(c => c.purchase_count > 0).length}
                </p>
              </div>
            </div>
          </div>
          <div className="lux-card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-900/30 text-amber-400">
                <Gift className="w-5 h-5" />
              </div>
              <div>
                <p className="text-lux-muted text-xs">Made Referrals</p>
                <p className="text-lux-text font-heading text-xl">
                  {customers.filter(c => c.referral_count > 0).length}
                </p>
              </div>
            </div>
          </div>
          <div className="lux-card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-900/30 text-yellow-400">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-lux-muted text-xs">Pending Deals</p>
                <p className="text-lux-text font-heading text-xl">
                  {customers.reduce((sum, c) => sum + (c.pending_count || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Customers List */}
        <div className="lux-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="lux-table-header">
                <tr>
                  <th className="px-6 py-4 text-left">Customer</th>
                  <th className="px-6 py-4 text-left">Contact</th>
                  <th className="px-6 py-4 text-center">Purchases</th>
                  <th className="px-6 py-4 text-center">Referrals</th>
                  <th className="px-6 py-4 text-center">Pending</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="lux-table-row" data-testid={`customer-row-${customer.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-lux-gold/20 flex items-center justify-center text-lux-gold font-semibold">
                          {customer.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-lux-text font-medium">{customer.name}</p>
                          {customer.address && (
                            <p className="text-lux-muted text-xs flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {customer.address}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {customer.phone && (
                          <p className="text-lux-text text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3 text-lux-muted" />
                            {customer.phone}
                          </p>
                        )}
                        {customer.email && (
                          <p className="text-lux-muted text-xs flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {customer.email}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        customer.purchase_count > 0 
                          ? 'bg-green-900/30 text-green-400' 
                          : 'bg-lux-border text-lux-muted'
                      }`}>
                        {customer.purchase_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        customer.referral_count > 0 
                          ? 'bg-amber-900/30 text-amber-400' 
                          : 'bg-lux-border text-lux-muted'
                      }`}>
                        {customer.referral_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {customer.pending_count > 0 ? (
                        <span className="px-2 py-1 rounded text-sm font-medium bg-yellow-900/30 text-yellow-400">
                          {customer.pending_count}
                        </span>
                      ) : (
                        <span className="text-lux-muted">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCustomerDetails(customer)}
                          className="text-lux-muted hover:text-lux-gold"
                          data-testid={`view-customer-${customer.id}`}
                        >
                          View Details
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                        {isMD && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(customer)}
                            className="text-lux-muted hover:text-red-400"
                            data-testid={`delete-customer-${customer.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredCustomers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-lux-muted">
                      {searchTerm ? 'No customers match your search' : 'No customers yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Customer Details Modal */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-lux-card border-lux-border">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-lux-gold flex items-center gap-3">
                {selectedCustomer && (
                  <>
                    <div className="w-12 h-12 rounded-full bg-lux-gold/20 flex items-center justify-center text-lux-gold font-bold text-xl">
                      {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                    </div>
                    {selectedCustomer.name}
                  </>
                )}
              </DialogTitle>
            </DialogHeader>

            {detailsLoading ? (
              <div className="py-12 text-center text-lux-muted">Loading customer details...</div>
            ) : customerDetails ? (
              <div className="space-y-6 py-4">
                {/* Customer Info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-lux-row rounded-lg">
                  <div>
                    <p className="text-lux-muted text-xs mb-1">Phone</p>
                    <p className="text-lux-text">{customerDetails.customer.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-lux-muted text-xs mb-1">Email</p>
                    <p className="text-lux-text">{customerDetails.customer.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-lux-muted text-xs mb-1">Address</p>
                    <p className="text-lux-text">{customerDetails.customer.address || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-lux-muted text-xs mb-1">Since</p>
                    <p className="text-lux-text">{formatDate(customerDetails.customer.created_at)}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 bg-green-900/10 border border-green-900/30 rounded-lg">
                    <p className="text-green-400 text-xs mb-1">Total Purchases</p>
                    <p className="text-green-400 font-heading text-2xl">{customerDetails.stats.total_purchases}</p>
                  </div>
                  <div className="p-4 bg-lux-gold/10 border border-lux-gold/30 rounded-lg">
                    <p className="text-lux-gold text-xs mb-1">Total Spent</p>
                    <p className="text-lux-gold font-heading text-lg">{formatCurrency(customerDetails.stats.total_spent)}</p>
                  </div>
                  <div className="p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                    <p className="text-amber-400 text-xs mb-1">Referrals Made</p>
                    <p className="text-amber-400 font-heading text-2xl">{customerDetails.stats.total_referrals}</p>
                  </div>
                  <div className="p-4 bg-blue-900/10 border border-blue-900/30 rounded-lg">
                    <p className="text-blue-400 text-xs mb-1">Referral Value</p>
                    <p className="text-blue-400 font-heading text-lg">{formatCurrency(customerDetails.stats.total_referral_value)}</p>
                  </div>
                </div>

                {/* Purchases */}
                <div>
                  <h3 className="text-lux-text font-medium mb-3 flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-green-400" />
                    Purchases ({customerDetails.purchases.length})
                  </h3>
                  {customerDetails.purchases.length > 0 ? (
                    <div className="space-y-2">
                      {customerDetails.purchases.map((purchase) => (
                        <div key={purchase.id} className="p-3 bg-lux-row rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Building2 className="w-4 h-4 text-lux-muted" />
                            <div>
                              <p className="text-lux-text text-sm">{purchase.property?.name || 'Unknown Property'}</p>
                              <p className="text-lux-muted text-xs">
                                {purchase.sale_code} • {formatDate(purchase.sale_date)} • {purchase.units_purchased || 1} unit(s)
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lux-gold font-semibold">{formatCurrency(purchase.sale_amount)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              purchase.approval_status === 'approved' 
                                ? 'bg-green-900/30 text-green-400' 
                                : purchase.approval_status === 'pending'
                                ? 'bg-yellow-900/30 text-yellow-400'
                                : 'bg-red-900/30 text-red-400'
                            }`}>
                              {purchase.approval_status || 'pending'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-lux-muted text-sm p-4 bg-lux-row rounded-lg text-center">No purchases yet</p>
                  )}
                </div>

                {/* Referrals */}
                <div>
                  <h3 className="text-lux-text font-medium mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-amber-400" />
                    Referrals Made ({customerDetails.referrals.length})
                  </h3>
                  {customerDetails.referrals.length > 0 ? (
                    <div className="space-y-2">
                      {customerDetails.referrals.map((referral) => (
                        <div key={referral.id} className="p-3 bg-lux-row rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Gift className="w-4 h-4 text-amber-400" />
                            <div>
                              <p className="text-lux-text text-sm">{referral.property?.name || 'Unknown Property'}</p>
                              <p className="text-lux-muted text-xs">
                                Referred: {referral.customer_name} • {formatDate(referral.sale_date)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-amber-400 font-semibold">{formatCurrency(referral.sale_amount)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              referral.approval_status === 'approved' 
                                ? 'bg-green-900/30 text-green-400' 
                                : referral.approval_status === 'pending'
                                ? 'bg-yellow-900/30 text-yellow-400'
                                : 'bg-red-900/30 text-red-400'
                            }`}>
                              {referral.approval_status || 'pending'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-lux-muted text-sm p-4 bg-lux-row rounded-lg text-center">No referrals yet</p>
                  )}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-lux-card border-lux-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lux-text">Delete Customer Details</AlertDialogTitle>
              <AlertDialogDescription className="text-lux-muted">
                Are you sure you want to delete details for "{customerToDelete?.name}"? This removes the customer profile from Customer Hub.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="lux-btn-secondary" data-testid="cancel-delete-customer-btn">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCustomer}
                disabled={deletingCustomer}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-customer-btn"
              >
                {deletingCustomer ? 'Deleting...' : 'Delete Details'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
